/**
 * SG（sugargirl）頁的資料形狀。
 *
 * 只放「畫面真的會用到」的欄位：後端 `/api/sg/girls` 與 connector 會多回很多東西
 * （站方原始欄位），一律在 `sgApi.ts` 收斂成這裡的型別，元件不直接碰原始 JSON。
 */
export type SgGirl = {
  id: string;
  nickname: string;
  unreadCount: number;
};

export type SgConversation = {
  id: string;
  customerName: string;
  /** 站方標記封鎖／已讀狀態；缺欄位就是 false。 */
  blocked: boolean;
  unreadCount: number;
  lastMessagePreview: string;
  lastActivityAt: number | null;
};

export type AutoReplyMode = 'draft' | 'auto';

/** 已接上的 session（＝後端 RegistryDO 裡 `service:'sg'` 的那幾筆）。 */
export type SgSession = {
  id: string;
  girlId: string;
  nickname: string;
  createdAt: number;
  /** 只在本頁建立時拿得到；重新整理後靠 `/auto-reply` 補。 */
  mode: AutoReplyMode;
};

/** `GET /api/sessions/:id/auto-reply` 的回應（只取畫面要的）。 */
export type AutoReplyStatus = {
  mode: AutoReplyMode;
  hasPersona: boolean;
  personaName: string | null;
  personaSource: string;
  repliesLastHour: number;
};

/** `draft` 事件：AI 擬稿、等人核可（後端 session-do 的 `#draft`）。 */
export type SgDraft = {
  id: string;
  text: string;
  ts: number;
  /** 這則草稿在回哪一則客人訊息（不一定有）。 */
  replyTo: string | null;
};

/** 模式的中文說明；兩者的差別必須寫在畫面上，否則使用者只能猜。 */
export const MODE_LABEL: Record<AutoReplyMode, string> = {
  draft: '擬稿（draft）',
  auto: '自動送出（auto）',
};

export const MODE_HINT: Record<AutoReplyMode, string> = {
  draft: 'AI 只寫草稿，不會送出。草稿留在對話串裡等人核可——驗收回覆品質用這個。',
  auto: 'AI 寫完直接送回 SG 站方，客人立刻收到。沒有核可步驟，適合已驗收過的人設。',
};

export function parseMode(value: unknown, fallback: AutoReplyMode = 'draft'): AutoReplyMode {
  return value === 'auto' || value === 'draft' ? value : fallback;
}

/** 最近 3 則草稿：新到舊（`limit` 只是防呆上限）。 */
export function recentDrafts(drafts: readonly SgDraft[], limit = 3): SgDraft[] {
  return [...drafts].sort((a, b) => b.ts - a.ts).slice(0, limit);
}

/** 相對時間（草稿時間用；超過一天就寫日期，不要一直顯示「36 小時前」）。 */
export function formatDraftTime(ts: number, now = Date.now()): string {
  const diff = Date.now() - ts;
  if (!Number.isFinite(ts) || diff < 0) return new Date(ts).toLocaleString('zh-TW');
  const min = Math.floor(diff / 60_000);
  if (min < 1) return '剛剛';
  if (min < 60) return `${min} 分鐘前`;
  const hour = Math.floor(min / 60);
  if (min < 60 * 24) return `${hour} 小時前`;
  void now;
  return new Date(ts).toLocaleString('zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}
